import { APP_INITIALIZER, Inject, Injectable, NgModule } from '@angular/core';
import { CommonModule, PathLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes, mapToCanActivate } from '@angular/router';
import { NgxsModule } from '@ngxs/store';
import {
    MissingTranslationHandler,
    MissingTranslationHandlerParams,
    TranslateLoader,
    TranslateModule,
} from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';

import { StyleService } from '@services/app-initialization/style.service';
import { FeatureService } from '@services/features/feature.service';
import { ToastService } from '@services/common/toast.service';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireConfigState } from '@state/questionnaire.config.state';
import { UserState } from '@state/user.state';
import { AppComponent } from './app.component';
import { AppDispatcherComponent } from './app-dispatcher.component';
import { AppLayoutModule } from './layout/app.layout.module';
import { ConfigService, DCAR_APP_CONFIG } from './app-config.service';
import { AuthenticationService, CognitoAuthenticationService } from './auth/service';
import { AuthInterceptor, JwtInterceptor, ErrorInterceptor, AuthGuard } from './auth/helpers';
import { AuthenticationModule } from './main/pages/authentication/authentication.module';
import { AppLayoutComponent } from './layout/app.layout.component';
import { SolutionState } from '@state/solution.state';
import { ComponentsModule } from './main/apps/components/components.module';
import { DCarbonizeAppConfig } from './@types/shared/app-config';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { environment } from 'environments/environment';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { LocalizationService } from '@services/app-initialization/localization.service';
import { OrganizationState } from '@state/organization.state';
import { AnalyticsService } from '@services/common/analytics.service';
import { GoogleTagManagerService } from '@services/common/google-tag-manager.service';

const cookieConfig: NgcCookieConsentConfig = {
    cookie: {
        domain: environment.domain,
    },
    position: 'bottom-right',
    theme: 'classic',
    palette: {
        popup: {
            background: '#2E6BF3',
            text: '#ffffff',
            link: '#ffffff',
        },
        button: {
            background: '#ffffff',
            text: '#2E6BF3',
        },
    },
    type: 'info',
};

@Injectable()
export class CustomTranslateLoader implements TranslateLoader {
    constructor(private http: HttpClient, @Inject(DCAR_APP_CONFIG) private config: DCarbonizeAppConfig) {}

    getTranslation(lang: string): Observable<any> {
        if (this.config.supportedLanguages.includes(lang)) {
            const baseTranslation$ = this.http.get(`./assets/i18n/${lang}.json`);
            if (
                this.config &&
                this.config.client &&
                this.config.client.name &&
                this.config.client.name.toLowerCase() !== 'dcarbonize'
            ) {
                const clientTranslation$ = this.http.get(
                    `./assets/i18n/${this.config.client.name.toLowerCase()}/${lang}.json`,
                );

                return forkJoin([baseTranslation$, clientTranslation$]).pipe(
                    map(([baseTranslation, clientTranslation]) => {
                        return this.mergeTranslations(baseTranslation, clientTranslation);
                    }),
                );
            } else {
                return baseTranslation$;
            }
        } else return of({});
    }

    private mergeTranslations(baseTranslation: any, clientTranslation: any): any {
        if (typeof baseTranslation !== 'object' || typeof clientTranslation !== 'object') {
            return clientTranslation;
        }

        const mergedTranslation = { ...baseTranslation };

        for (const key of Object.keys(clientTranslation)) {
            if (
                mergedTranslation.hasOwnProperty(key) &&
                typeof mergedTranslation[key] === 'object' &&
                typeof clientTranslation[key] === 'object'
            ) {
                mergedTranslation[key] = this.mergeTranslations(mergedTranslation[key], clientTranslation[key]);
            } else {
                mergedTranslation[key] = clientTranslation[key];
            }
        }

        return mergedTranslation;
    }
}

@Injectable()
export class CustomMissingTranslationHandler implements MissingTranslationHandler {
    constructor(private http: HttpClient) {}

    handle(params: MissingTranslationHandlerParams): Observable<string> {
        if (!environment.production) {
            const translationFallbackPath: string = './assets/i18n/newKeys.json';

            return this.http.get(translationFallbackPath).pipe(
                catchError(() => of(`MISSING TRANSLATION KEY: ${params.key}`)),
                map((missingTranslations) => {
                    console.log('Missing translation key:', params.key);
                    return this.getTranslation(params.key, missingTranslations);
                }),
            );
        } else {
            return of(`MISSING TRANSLATION KEY: ${params.key}`);
        }
    }

    private getTranslation(key: string, translations: any): string {
        const keys: string[] = key.split('.');
        let result: any = translations;

        for (const k of keys) {
            result = result[k];
            if (!result) return `MISSING TRANSLATION KEY: ${k}`;
        }

        return result;
    }
}

const appRoutes: Routes = [
    {
        path: '',
        component: AppLayoutComponent,
        children: [
            { path: '', component: AppDispatcherComponent },
            {
                path: 'app',
                loadChildren: () => import('./main/apps/apps.module').then((m) => m.AppsModule),
                canActivate: mapToCanActivate([AuthGuard]),
            },
            {
                path: 'admin',
                loadChildren: () => import('./main/admin/admin.module').then((m) => m.AdminModule),
                canActivate: mapToCanActivate([AuthGuard]),
            },
            {
                path: 'dedicated_admin',
                loadChildren: () =>
                    import('./main/dedicated_admin/dedicated_admin.module').then((m) => m.DedicatedAdminModule),
                canActivate: mapToCanActivate([AuthGuard]),
            },
        ],
    },
    {
        path: 'auth',
        loadChildren: () =>
            import('./main/pages/authentication/authentication.module').then((m) => m.AuthenticationModule),
    },
    {
        path: 'pages', // to check
        loadChildren: () => import('./main/pages/pages.module').then((m) => m.PagesModule),
    },
    {
        path: '**',
        redirectTo: '/pages/not-found',
    },
];

@NgModule({
    declarations: [AppComponent, AppDispatcherComponent],
    imports: [
        CommonModule,
        ComponentsModule,
        BrowserModule,
        AppLayoutModule,
        BrowserAnimationsModule,
        AuthenticationModule,
        HttpClientModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoader,
                deps: [HttpClient, DCAR_APP_CONFIG],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: CustomMissingTranslationHandler,
                deps: [HttpClient],
            },
        }),
        NgcCookieConsentModule.forRoot(cookieConfig),
        NgxsModule.forRoot([UserState, QuestionnaireConfigState, QuestionnaireState, SolutionState, OrganizationState]),
        RouterModule.forRoot(appRoutes, {
            enableTracing: environment.debug?.tracing ?? false,
            scrollPositionRestoration: 'enabled',
        }),
        // PrimeNG modules
        MessagesModule,
        ToastModule,
    ],
    providers: [
        LocalizationService,
        StyleService,
        { provide: DCAR_APP_CONFIG, useValue: environment },
        {
            provide: APP_INITIALIZER,
            useFactory: (localizationService: LocalizationService) => localizationService.initializeApp(),
            multi: true,
            deps: [LocalizationService],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (styleService: StyleService) => styleService.initializeApp(),
            multi: true,
            deps: [StyleService],
        },
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: ConfigService, useClass: ConfigService, deps: [DCAR_APP_CONFIG] },
        { provide: FeatureService, useClass: FeatureService },
        { provide: AuthenticationService, useClass: CognitoAuthenticationService },
        { provide: AnalyticsService, useClass: GoogleTagManagerService },
        { provide: MessageService },
        { provide: ToastService },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
